/* iter194 — Break Timer with alert.
   ------------------------------------------------------------
   Contract:
     - `breakStartedAt` : ISO string from the server for when this break began.
     - `alertMinutes`   : company setting; play audible + visual alert when
                          elapsed >= alertMinutes.
     - Timer keeps ticking accurately across refresh, phone lock, and tab
       switches because the "start" anchor comes from the server (persisted in
       Mongo), NOT a client-side counter that would drift.
     - Audible alert fires ONCE per break (deduped in localStorage using the
       break start ISO as the key). If the alert would have fired while the tab
       was closed, we still surface the visual pulse the moment we mount.
   ------------------------------------------------------------ */

import React, { useEffect, useState, useRef } from 'react';
import { Coffee, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Data-URL beep so we don't need to ship an mp3 asset.
// 0.5s 880 Hz sine wave WAV encoded as base64. Kept short so the audible cue
// doesn't feel jarring on the shop floor.
const BEEP_DATA_URL =
  'data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

const fmtElapsed = (secs) => {
  if (secs < 0) secs = 0;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export const BreakTimer = ({ breakStartedAt, alertMinutes = 30 }) => {
  const [now, setNow] = useState(() => Date.now());
  const alertedRef = useRef(false);

  // Re-tick every second while the component is mounted. The server anchor is
  // authoritative — if the browser was closed, `now` still becomes accurate
  // the moment we remount because we compare against the server start ISO.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const startMs = breakStartedAt ? new Date(breakStartedAt).getTime() : null;
  const elapsedSec = startMs ? Math.max(0, Math.floor((now - startMs) / 1000)) : 0;
  const thresholdSec = Math.max(1, Number(alertMinutes || 30)) * 60;
  const overThreshold = !!startMs && elapsedSec >= thresholdSec;

  // Fire the audible alert the moment we cross the threshold. Dedup per break
  // via localStorage so a refresh doesn't retrigger the beep.
  useEffect(() => {
    if (!breakStartedAt || !overThreshold || alertedRef.current) return;
    const key = `nexflow_break_alerted_${breakStartedAt}`;
    if (typeof window !== 'undefined' && !window.localStorage.getItem(key)) {
      try {
        const a = new Audio(BEEP_DATA_URL);
        a.play().catch(() => { /* iOS autoplay policy — swallowed */ });
        // Some devices ignore very short beeps, so a second nudge helps.
        setTimeout(() => { try { new Audio(BEEP_DATA_URL).play().catch(() => {}); } catch {/*noop*/} }, 800);
      } catch { /* no-op — visual alert still fires below */ }
      try {
        window.localStorage.setItem(key, String(Date.now()));
      } catch { /* private-mode / storage denied */ }
    }
    toast.error(`Break is over — you've been on break ${alertMinutes} min`, {
      duration: 15000,
    });
    alertedRef.current = true;
  }, [overThreshold, breakStartedAt, alertMinutes]);

  if (!breakStartedAt) return null;

  const displayColor = overThreshold ? 'border-red-500 bg-red-500/10' : 'border-[#D4AF37] bg-[#D4AF37]/5';
  const textColor = overThreshold ? 'text-red-300' : 'text-[#D4AF37]';

  return (
    <div
      data-testid="break-timer-card"
      data-over-threshold={overThreshold ? '1' : '0'}
      className={`border rounded-md p-3 flex items-center justify-between gap-3 flex-wrap ${displayColor} ${overThreshold ? 'animate-pulse' : ''}`}
    >
      <div className="flex items-center gap-2">
        {overThreshold ? (
          <AlertTriangle className={`w-5 h-5 ${textColor}`} />
        ) : (
          <Coffee className={`w-5 h-5 ${textColor}`} />
        )}
        <div>
          <p className={`text-xs uppercase tracking-wider font-bold ${textColor}`} data-testid="break-timer-label">
            {overThreshold ? 'Break is over!' : 'On Break'}
          </p>
          <p className="text-white text-2xl font-bold font-mono" data-testid="break-timer-elapsed">
            {fmtElapsed(elapsedSec)}
          </p>
        </div>
      </div>
      <p className="text-xs text-[#A3A3A3]" data-testid="break-timer-threshold">
        Alert at <span className="text-white font-bold">{alertMinutes}:00</span>
      </p>
    </div>
  );
};

export default BreakTimer;
